import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded"
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import Grid2 from "@mui/material/Grid2"
import { doc, getDoc } from "firebase/firestore"
import { httpsCallable } from "firebase/functions"
import { QRCodeCanvas } from "qrcode.react"
import { Theme as MuiTheme } from "@rjsf/material-ui"
import { RJSFSchema } from "@rjsf/utils"
import { withTheme } from "@rjsf/core"
import validator from "@rjsf/validator-ajv8"

import { db, functions } from "../firebase"
import { removeUndefinedFields } from "../utils"
import { LoadingService } from "react-ui-components/components/LoadingService"
import CustomObjectFieldTabs from "./raffle/CustomObjectFieldTabs"
import { DEFAULT_PREPAY_TICKET_CODE_PREFIX, useAppConfiguration } from "./AppConfigurationService"
import { string } from "react-ui-components/utils/schema"

const generateNewPrepayTickets = httpsCallable(functions, "generateNewPrepayTickets")

const uiSchema = {
  name: {
    "ui:placeholder": "e.g. VIP prepay batch - Main entrance",
    "ui:autofocus": true,
  },
  codeSettings: {
    codePrefix: {
      "ui:help":
        "Optional text added in front of the raw compact token when generating the final scan code.",
      "ui:placeholder": "e.g. VIP-",
    },
  },
  raffleTicketsCount: {
    "ui:help": "How many raffle tickets one scan should grant. Defaults to 1.",
    "ui:placeholder": "e.g. 5",
  },
  userScanCount: {
    "ui:help": "Leave blank or zero for an infinite amount",
    "ui:placeholder": "e.g. 1",
  },
  scanCooldownMinutes: {
    "ui:help": "Minutes a user must wait before scanning the same QR again. Defaults to 60.",
    "ui:placeholder": "e.g. 60",
  },
  ticketTypes: {
    "ui:widget": "checkboxes",
    "ui:help": "Select at least one ticket type to generate.",
  },
  ticketsPointsValue: {
    "ui:options": {
      variant: "outlined",
    },
    "ui:help": "Optional monetary value per ticket.",
    "ui:placeholder": "e.g. 50",
  },
  ticketsPoints: {
    "ui:options": {
      variant: "outlined",
    },
    "ui:help": "Optional points granted per ticket.",
    "ui:placeholder": "e.g. 10",
  },
}

const ThemedForm = withTheme(MuiTheme)

type TicketType = {
  name: string
  title: string
}

type GeneratedTicketPreview = {
  availableCount: number | null
  qrCode: string
  rawQrCode: string
  codePrefix: string
  name: string
  sundryQrId: string | null
  sundryQrStatus: string | null
  sundryShortCode: string | null
  sundryShortUrl: string | null
  ticketTypes: string[]
  ticketsCount: number
  raffleTicketsCount: number | null
  userScanCount: number | null
  scanCooldownMinutes: number | null
  ticketsPointsValue: number | null
  ticketsPoints: number | null
  unlimitedTotalScans?: boolean
}

type SubmitFeedback = {
  severity: "success" | "error"
  message: string
}

const VALIDATION_LABELS: Record<string, string> = {
  name: "batch name",
  ticketTypes: "ticket type selection",
  raffleTicketsCount: "tickets granted per scan",
  userScanCount: "user scan limit",
  scanCooldownMinutes: "scan cooldown",
  ticketsPointsValue: "value",
  ticketsPoints: "points",
}

const MIN_TICKETS = 1
const MAX_TICKETS = 5000
const DEFAULT_SCAN_COOLDOWN_MINUTES = 60

const isUnlimitedTotalScansValue = (value: unknown) =>
  value === true || value === -1 || value === "true" || value === "unlimited"

const toOptionalNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const normalizeCodePrefix = (value: unknown) => {
  if (typeof value !== "string") {
    return ""
  }

  return value.trim()
}

const getCodePrefix = (value: Record<string, any> | null | undefined) =>
  normalizeCodePrefix(value?.codeSettings?.codePrefix)

const buildScanCode = (rawQrCode: string, codePrefix: string) =>
  codePrefix ? `${codePrefix}${rawQrCode}` : rawQrCode

const getCallableErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object") {
    const nextError = error as {
      message?: string
      details?: unknown
    }

    if (typeof nextError.message === "string" && nextError.message.trim()) {
      return nextError.message.replace(/^.*?:\s*/, "").trim()
    }

    if (typeof nextError.details === "string" && nextError.details.trim()) {
      return nextError.details.trim()
    }
  }

  return fallback
}

const normalizeTicketTypes = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((item): item is string => typeof item === "string" && !!item.trim())
}

const normalizeTicketTypeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "")

const getTicketTypeKeys = (selectedTypes: string[], configuredTicketTypes: TicketType[]) => {
  const uniqueKeys = new Set<string>()

  selectedTypes.forEach((selectedType) => {
    const normalizedSelectedType = selectedType.trim()
    const matchedTicketType = configuredTicketTypes.find(
      (type) => typeof type?.title === "string" && type.title.trim() === normalizedSelectedType,
    )

    const key = matchedTicketType?.name || matchedTicketType?.title || normalizedSelectedType
    if (typeof key === "string" && key.trim()) {
      uniqueKeys.add(key.trim())
    }
  })

  return Array.from(uniqueKeys)
}

const hasRafflePrepayType = (ticketTypeKeys: string[]) =>
  ticketTypeKeys.some((ticketTypeKey) => normalizeTicketTypeKey(ticketTypeKey) === "raffleprepay")

const toOptionalPositiveInteger = (value: unknown): number | null => {
  const parsedValue = toOptionalNumber(value)

  if (parsedValue === null || !Number.isInteger(parsedValue) || parsedValue < 1) {
    return null
  }

  return parsedValue
}

const toOptionalNonNegativeInteger = (value: unknown): number | null => {
  const parsedValue = toOptionalNumber(value)

  if (parsedValue === null || !Number.isInteger(parsedValue) || parsedValue < 0) {
    return null
  }

  return parsedValue
}

const formatValidationMessage = (errors: Array<Record<string, any>>) => {
  const labels = Array.from(
    new Set(
      errors
        .map((error) => {
          const fieldKey =
            (typeof error?.params?.missingProperty === "string" && error.params.missingProperty) ||
            (typeof error?.property === "string" ? error.property.replace(/^\./, "") : "")

          return VALIDATION_LABELS[fieldKey]
        })
        .filter(Boolean),
    ),
  )

  if (!labels.length) {
    return "Please review the highlighted fields before generating tickets."
  }

  if (labels.length === 1) {
    return `Please review the ${labels[0]} field before generating tickets.`
  }

  const leadingLabels = labels.slice(0, -1).join(", ")
  const trailingLabel = labels[labels.length - 1]
  return `Please review the ${leadingLabels} and ${trailingLabel} fields before generating tickets.`
}
interface QueryParams {
  lang: string
  country: string
  state: string
  city: string
  other: string
}

const getQueryParams = (value: Record<string, any> | null | undefined): QueryParams => ({
  lang: typeof value?.lang === "string" ? value.lang.trim() : "",
  country: typeof value?.country === "string" ? value.country.trim() : "",
  state: typeof value?.state === "string" ? value.state.trim() : "",
  city: typeof value?.city === "string" ? value.city.trim() : "",
  other: typeof value?.other === "string" ? value.other.trim() : "",
})

const QUERY_PARAM_KEYS: Array<keyof QueryParams> = ["lang", "country", "state", "city", "other"]

const TicketForm = ({ raffleId, onSuccess }: any) => {
  const { configuration } = useAppConfiguration()
  const [ticketsCount, setTicketsCount] = useState<number>(1)
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([
    { name: "RafflePrepay", title: "Raffle Prepay" },
  ])
  const [formData, setFormData] = useState<Record<string, any>>({})
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [generatedTicket, setGeneratedTicket] = useState<GeneratedTicketPreview | null>(null)
  const [unlimitedTotalScans, setUnlimitedTotalScans] = useState(false)
  const [submitFeedback, setSubmitFeedback] = useState<SubmitFeedback | null>(null)
  const [copyFeedback, setCopyFeedback] = useState<{
    severity: "success" | "warning"
    message: string
  } | null>(null)
  const otherProps = useMemo(() => getQueryParams(formData?.codeSettings), [formData])
  const lastAutoPrefixRef = useRef<string>("")
  const previousOtherPropsRef = useRef<QueryParams>(getQueryParams(undefined))
  const previousConfiguredDefaultCodePrefixRef = useRef<string>("")

  function replaceDefaultCodePrefix(params: QueryParams, str: string): string {
    str.replace(/{{(.*?)}}/g, (_, key) => {
      const value = params[key as keyof QueryParams] || ""
      str = str.replace(`{{${key}}}`, `${key}=${value}`)
      return value
    })
    return str
  }

  const configuredDefaultCodePrefix = useMemo(
    () =>
      normalizeCodePrefix((configuration as any)?.prepayTicketCodePrefixDefault) ||
      DEFAULT_PREPAY_TICKET_CODE_PREFIX,
    [configuration],
  )

  useEffect(() => {
    const changedKeys = QUERY_PARAM_KEYS.filter(
      (key) => previousOtherPropsRef.current[key] !== otherProps[key],
    )
    const configuredPrefixChanged =
      previousConfiguredDefaultCodePrefixRef.current !== configuredDefaultCodePrefix
    const previousAutoPrefix = replaceDefaultCodePrefix(
      previousOtherPropsRef.current,
      configuredDefaultCodePrefix,
    )
    const autoPrefix = replaceDefaultCodePrefix(otherProps, configuredDefaultCodePrefix)

    setFormData((prev) => {
      const currentPrefix = getCodePrefix(prev)
      const canOverwriteCurrentPrefix =
        !currentPrefix ||
        currentPrefix === lastAutoPrefixRef.current ||
        currentPrefix === previousAutoPrefix
      const hasRelevantChangedPlaceholder = changedKeys.some((key) =>
        configuredDefaultCodePrefix.includes(`{{${key}}}`),
      )

      if (currentPrefix === autoPrefix) {
        lastAutoPrefixRef.current = currentPrefix
        return prev
      }

      if (!canOverwriteCurrentPrefix) {
        return prev
      }

      if (currentPrefix && !configuredPrefixChanged && !hasRelevantChangedPlaceholder) {
        return prev
      }

      lastAutoPrefixRef.current = autoPrefix

      return {
        ...prev,
        codeSettings: {
          ...(prev?.codeSettings || {}),
          codePrefix: autoPrefix,
        },
      }
    })

    previousOtherPropsRef.current = otherProps
    previousConfiguredDefaultCodePrefixRef.current = configuredDefaultCodePrefix
  }, [configuredDefaultCodePrefix, otherProps])

  useEffect(() => {
    const fetchTicketTypesConfig = async () => {
      try {
        setLoading(true)
        setFetchError(null)
        const querySnapshot = await getDoc(doc(db, "app-configurations", "rafflesTicketsTypes"))
        const data = querySnapshot.data()?.collection as TicketType[] | undefined

        if (!data?.length) {
          setTicketTypes([])
          setFetchError("No ticket types are configured yet.")
        } else {
          setTicketTypes(data)
        }
      } catch (error) {
        console.error("Error fetching ticket types configuration:", error)
        setFetchError("We could not load ticket types. Please try again.")
      } finally {
        setLoading(false)
      }
    }

    fetchTicketTypesConfig()
  }, [])

  const ticketSchema: RJSFSchema | null = useMemo(() => {
    if (!ticketTypes.length) {
      return null
    }

    return {
      title: "Ticket",
      type: "object",
      required: ["ticketTypes", "name"],
      properties: {
        name: {
          type: "string",
          title: "Ticket Name or Description",
          minLength: 2,
          maxLength: 100,
          description: "A short name or description to identify this ticket batch.",
        },
        codeSettings: {
          type: "object",
          title: "Code",
          description:
            "Build an alternate scan code by prepending text to the raw compact QR token.",
          properties: {
            codePrefix: {
              type: "string",
              title: "Code Prefix",
              maxLength: 500,
              description: "When set, the generated scan code becomes {prefix}{raw compact token}.",
            },
            lang: string("Language"),
            country: string("Country"),
            state: string("State"),
            city: string("City"),
            other: string("Other"),
          },
        },
        ticketTypes: {
          type: "array",
          title: "Ticket Types",
          minItems: 1,
          uniqueItems: true,
          items: {
            type: "string",
            enum: ticketTypes.map((x) => x.title),
          },
        },
        userScanCount: { type: "integer", title: "User Scan Limit", minimum: 0 },
        scanCooldownMinutes: { type: "integer", title: "Scan Cooldown (Minutes)", minimum: 0 },
        ticketsPointsValue: { type: "number", title: "Value", minimum: 0 },
        ticketsPoints: { type: "number", title: "Points", minimum: 0 },
      },
      dependencies: {
        ticketTypes: {
          allOf: ticketTypes.map((type) => {
            const key = (type.name || type.title || "ticket").toLowerCase()
            const normalizedTypeKey = normalizeTicketTypeKey(type.name || type.title || "ticket")
            const dependentProperties: Record<string, RJSFSchema> = {
              [`${key}Property`]: {
                type: "string",
                title: `${type.title || type.name} Property`,
                description:
                  "Optional extra detail for this ticket type (e.g. batch, tier, or notes).",
              },
            }

            if (normalizedTypeKey === "raffleprepay") {
              dependentProperties.raffleTicketsCount = {
                type: "integer",
                title: "Tickets Granted Per Scan",
                minimum: 1,
                description:
                  "How many raffle tickets this batch should issue whenever it is scanned.",
              }
            }

            return {
              if: {
                properties: {
                  ticketTypes: { contains: { const: type.title } },
                },
              },
              then: {
                properties: dependentProperties,
              },
            }
          }),
        },
      },
    }
  }, [ticketTypes])

  const activeTicketTypes = useMemo(() => normalizeTicketTypes(formData.ticketTypes), [formData])
  const activeTicketTypeKeys = useMemo(
    () => getTicketTypeKeys(activeTicketTypes, ticketTypes),
    [activeTicketTypes, ticketTypes],
  )
  const hasActiveRafflePrepay = useMemo(
    () => hasRafflePrepayType(activeTicketTypeKeys),
    [activeTicketTypeKeys],
  )
  const activeTicketName = typeof formData.name === "string" ? formData.name.trim() : ""
  const activeCodePrefix = getCodePrefix(formData)
  const activeRaffleTicketsCount = toOptionalPositiveInteger(formData.raffleTicketsCount)
  const activeUserScanCount = toOptionalNonNegativeInteger(formData.userScanCount)
  const activeScanCooldownMinutes = toOptionalNonNegativeInteger(formData.scanCooldownMinutes)
  const activeValue = toOptionalNumber(formData.ticketsPointsValue)
  const activePoints = toOptionalNumber(formData.ticketsPoints)

  const previewName = generatedTicket?.name || activeTicketName
  const previewCodePrefix = generatedTicket?.codePrefix ?? activeCodePrefix
  const previewTicketTypes = generatedTicket?.ticketTypes?.length
    ? generatedTicket.ticketTypes
    : activeTicketTypes
  const previewUnlimitedTotalScans =
    generatedTicket?.unlimitedTotalScans ?? unlimitedTotalScans
  const previewTicketsCount = generatedTicket?.ticketsCount ?? ticketsCount
  const previewRaffleTicketsCount =
    generatedTicket?.raffleTicketsCount ??
    (hasActiveRafflePrepay ? activeRaffleTicketsCount ?? 1 : null)
  const previewUserScanCount = generatedTicket?.userScanCount ?? activeUserScanCount
  const previewScanCooldownMinutes =
    generatedTicket?.scanCooldownMinutes ??
    activeScanCooldownMinutes ??
    DEFAULT_SCAN_COOLDOWN_MINUTES
  const previewValue = generatedTicket?.ticketsPointsValue ?? activeValue
  const previewPoints = generatedTicket?.ticketsPoints ?? activePoints
  const rawQrCode = generatedTicket?.rawQrCode || ""
  const scanCode = generatedTicket?.qrCode || ""

  const handleSubmit = useCallback(
    async ({ formData }: any) => {
      if (!raffleId) {
        setSubmitFeedback({
          severity: "error",
          message: "Raffle ID is required before generating tickets.",
        })
        return
      }

      setGeneratedTicket(null)
      setCopyFeedback(null)
      setSubmitFeedback(null)
      setIsSubmitting(true)

      const sanitizedData = (removeUndefinedFields(formData) || {}) as Record<string, any>
      const selectedTicketTypes = normalizeTicketTypes(sanitizedData.ticketTypes)
      const selectedTicketTypeKeys = getTicketTypeKeys(selectedTicketTypes, ticketTypes)
      const codePrefix = getCodePrefix(sanitizedData)
      const batchName = typeof sanitizedData.name === "string" ? sanitizedData.name.trim() : ""

      // Validate required data before sending
      if (!raffleId || raffleId.trim().length === 0) {
        setSubmitFeedback({
          severity: "error",
          message: "Invalid raffle ID. Please refresh and try again.",
        })
        setIsSubmitting(false)
        LoadingService.setLoading(false)
        return
      }

      if (!unlimitedTotalScans && (!ticketsCount || ticketsCount < 1)) {
        setSubmitFeedback({
          severity: "error",
          message: "Invalid ticket count. Tickets count must be at least 1.",
        })
        setIsSubmitting(false)
        LoadingService.setLoading(false)
        return
      }

      if (!selectedTicketTypes || selectedTicketTypes.length === 0) {
        setSubmitFeedback({
          severity: "error",
          message: "Please select at least one ticket type before generating.",
        })
        setIsSubmitting(false)
        LoadingService.setLoading(false)
        return
      }

      const raffleTicketsCount = hasRafflePrepayType(selectedTicketTypeKeys)
        ? toOptionalPositiveInteger(sanitizedData.raffleTicketsCount) ?? 1
        : null
      const propertyData = Object.fromEntries(
        Object.entries(sanitizedData).filter(([key, value]) => {
          if (!/Property$/i.test(key)) {
            return false
          }
          if (value === null || value === undefined) {
            return false
          }

          return String(value).trim().length > 0
        }),
      )

      if (Object.keys(propertyData).length > 0) {
        console.warn("Including dynamic properties in request:", propertyData)
      }

      const ticketData = {
        raffleId,
        name: batchName,
        ...(batchName ? { batchName } : {}),
        ticketTypes: selectedTicketTypes,
        ticketTypeKeys: selectedTicketTypeKeys,
        raffleTicketsCount,
        ticketsPointsValue: toOptionalNumber(sanitizedData.ticketsPointsValue),
        ticketsPoints: toOptionalNumber(sanitizedData.ticketsPoints),
        userScanCount: toOptionalNonNegativeInteger(sanitizedData.userScanCount),
        scanCooldownMinutes:
          toOptionalNonNegativeInteger(sanitizedData.scanCooldownMinutes) ??
          DEFAULT_SCAN_COOLDOWN_MINUTES,
        ...(codePrefix ? { codePrefix } : {}),
        unlimitedTotalScans,
        ...propertyData,
      }

      try {
        LoadingService.setLoading(true)
        const payloadToSend = {
          ...ticketData,
          ...(unlimitedTotalScans ? {} : { ticketsCount }),
        }
        console.log("Sending ticket generation request with payload:", payloadToSend)
        const result: any = await generateNewPrepayTickets(payloadToSend)
        const generatedData = result?.data
        const nextRawQrCode =
          typeof generatedData === "string"
            ? generatedData
            : typeof generatedData?.rawQrCodeData === "string"
            ? generatedData.rawQrCodeData
            : typeof generatedData?.qrCodeData === "string"
            ? generatedData.qrCodeData
            : ""
        const nextCodePrefix =
          typeof generatedData?.codePrefix === "string" ? generatedData.codePrefix : codePrefix
        const nextScanCode =
          typeof generatedData === "string"
            ? buildScanCode(nextRawQrCode, nextCodePrefix)
            : typeof generatedData?.qrCodeData === "string"
            ? generatedData.qrCodeData
            : buildScanCode(nextRawQrCode, nextCodePrefix)

        setGeneratedTicket({
          availableCount:
            isUnlimitedTotalScansValue(generatedData?.unlimitedTotalScans) ||
            isUnlimitedTotalScansValue(generatedData?.availableCount)
              ? -1
              : toOptionalNonNegativeInteger(generatedData?.availableCount) ?? ticketsCount,
          qrCode: nextScanCode,
          rawQrCode: nextRawQrCode,
          codePrefix: nextCodePrefix,
          name: typeof generatedData?.name === "string" ? generatedData.name : ticketData.name || "",
          sundryQrId: typeof generatedData?.sundryQrId === "string" ? generatedData.sundryQrId : null,
          sundryQrStatus:
            typeof generatedData?.sundryQrStatus === "string" ? generatedData.sundryQrStatus : null,
          sundryShortCode:
            typeof generatedData?.sundryShortCode === "string" ? generatedData.sundryShortCode : null,
          sundryShortUrl:
            typeof generatedData?.sundryShortUrl === "string" ? generatedData.sundryShortUrl : null,
          ticketTypes: Array.isArray(generatedData?.ticketTypes) ? generatedData.ticketTypes : selectedTicketTypes,
          ticketsCount: toOptionalNonNegativeInteger(generatedData?.ticketsCount) ?? (unlimitedTotalScans ? 1 : ticketsCount),
          raffleTicketsCount:
            toOptionalPositiveInteger(generatedData?.raffleTicketsCount) ?? raffleTicketsCount,
          userScanCount: toOptionalNonNegativeInteger(generatedData?.userScanCount),
          scanCooldownMinutes:
            toOptionalNonNegativeInteger(generatedData?.scanCooldownMinutes) ??
            toOptionalNonNegativeInteger(ticketData.scanCooldownMinutes) ??
            DEFAULT_SCAN_COOLDOWN_MINUTES,
          ticketsPointsValue:
            toOptionalNumber(generatedData?.ticketsPointsValue) ??
            toOptionalNumber(generatedData?.value) ??
            toOptionalNumber(ticketData.ticketsPointsValue),
          ticketsPoints:
            toOptionalNumber(generatedData?.ticketsPoints) ??
            toOptionalNumber(generatedData?.points) ??
            toOptionalNumber(ticketData.ticketsPoints),
          unlimitedTotalScans:
            isUnlimitedTotalScansValue(generatedData?.unlimitedTotalScans) ||
            unlimitedTotalScans,
        })
        setSubmitFeedback({
          severity: "success",
          message: `${ticketData.name || "Ticket batch"} generated successfully.`,
        })
        setFormData(formData)
        onSuccess && onSuccess(result)
      } catch (error) {
        console.error("Error generating tickets - Full error object:", error)
        if (typeof error === "object" && error !== null) {
          console.error("Error details:", {
            message: (error as any).message,
            code: (error as any).code,
            details: (error as any).details,
            toString: String(error),
          })
        }
        const errorMsg = getCallableErrorMessage(
          error,
          "We could not generate tickets. Please try again.",
        )
        console.error("Final error message to show user:", errorMsg)
        setSubmitFeedback({
          severity: "error",
          message: errorMsg,
        })
      } finally {
        LoadingService.setLoading(false)
        setIsSubmitting(false)
      }
    },
    [onSuccess, raffleId, ticketTypes, ticketsCount, unlimitedTotalScans],
  )

  const handleCopyQrCode = useCallback(async () => {
    if (!scanCode) {
      return
    }

    try {
      await navigator.clipboard.writeText(scanCode)
      setCopyFeedback({
        severity: "success",
        message: "Generated scan code copied.",
      })
    } catch (error) {
      console.error("Unable to copy scan code:", error)
      setCopyFeedback({
        severity: "warning",
        message:
          "Copy is unavailable here. You can still copy the generated scan code manually from the field.",
      })
    }
  }, [scanCode])

  const handleCopyRawQrCode = useCallback(async () => {
    if (!rawQrCode) {
      return
    }

    try {
      await navigator.clipboard.writeText(rawQrCode)
      setCopyFeedback({
        severity: "success",
        message: "Raw compact ticket token copied.",
      })
    } catch (error) {
      console.error("Unable to copy raw ticket token:", error)
      setCopyFeedback({
        severity: "warning",
        message:
          "Copy is unavailable here. You can still copy the raw compact token manually from the field.",
      })
    }
  }, [rawQrCode])

  const handleTicketsCountChange = (value: string) => {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) {
      setTicketsCount(MIN_TICKETS)
      return
    }

    setTicketsCount(Math.min(Math.max(parsed, MIN_TICKETS), MAX_TICKETS))
  }

  const handleUnlimitedTotalScansChange = (checked: boolean) => {
    setUnlimitedTotalScans(checked)
    setSubmitFeedback(null)
  }

  const handleValidationError = useCallback((errors: Array<Record<string, any>>) => {
    setSubmitFeedback({
      severity: "error",
      message: formatValidationMessage(errors),
    })
  }, [])

  return (
    <Grid2 container spacing={3} alignItems="stretch">
      <Grid2 size={{ xs: 12, lg: 7 }}>
        <Card sx={{ height: "100%" }}>
          <CardHeader
            title="Generate Ticket Batch"
            subheader="Create a reusable prepay QR code, label it clearly, and keep it easy to find later."
          />
          {loading ? <LinearProgress /> : null}
          <CardContent>
            <Stack spacing={3}>
              {fetchError ? <Alert severity="error">{fetchError}</Alert> : null}
              {submitFeedback ? (
                <Alert severity={submitFeedback.severity}>{submitFeedback.message}</Alert>
              ) : null}
              {!ticketSchema && !loading ? (
                <Alert severity="warning">Ticket types are not configured.</Alert>
              ) : null}

              {ticketSchema ? (
                <>
                  <Alert severity="info">
                    Name the batch before generating it so the ticket list can be searched by that
                    label.
                  </Alert>
                  <ThemedForm
                  
                    uiSchema={uiSchema}
                    schema={ticketSchema}
                    onSubmit={handleSubmit}
                    onError={handleValidationError}
                    formData={formData}
                    onChange={({ formData }) => {
                      setFormData(formData)
                      setSubmitFeedback(null)
                    }}
                    validator={validator}
                    templates={{ ObjectFieldTemplate: CustomObjectFieldTabs }}
                    disabled={isSubmitting || loading}
                    showErrorList={false}
                    focusOnFirstError
                  >
                    <Stack spacing={3} sx={{ mt: 2 }}>
                      <Paper
                        variant="outlined"
                        sx={{ p: 2.5, borderRadius: 3, backgroundColor: "grey.50" }}
                      >
                        <Stack spacing={2}>
                          <Typography variant="subtitle2">Batch settings</Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={unlimitedTotalScans}
                                onChange={(event) =>
                                  handleUnlimitedTotalScansChange(event.target.checked)
                                }
                              />
                            }
                            label="Unlimited total QR scans"
                          />
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ sm: "flex-start" }}
                          >
                            <TextField
                              value={ticketsCount}
                              label="Quantity"
                              type="number"
                              disabled={unlimitedTotalScans}
                              onChange={(event) => handleTicketsCountChange(event.target.value)}
                              inputProps={{ min: MIN_TICKETS, max: MAX_TICKETS }}
                              helperText={
                                unlimitedTotalScans
                                  ? "The QR code can be scanned an unlimited number of times."
                                  : `Between ${MIN_TICKETS} and ${MAX_TICKETS}`
                              }
                              fullWidth
                            />
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              disabled={isSubmitting || loading}
                              sx={{ minWidth: { sm: 220 } }}
                            >
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="center"
                              >
                                {isSubmitting ? (
                                  <CircularProgress size={18} color="inherit" />
                                ) : null}
                                <span>{isSubmitting ? "Generating..." : "Generate Tickets"}</span>
                              </Stack>
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Stack>
                  </ThemedForm>
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Grid2>
      <Grid2 size={{ xs: 12, lg: 5 }}>
        <Card sx={{ height: "100%" }}>
          <CardHeader
            title={scanCode ? "Generated QR" : "Batch Preview"}
            subheader={
              scanCode
                ? "This preview matches the last generated ticket batch."
                : "Review the batch details before generating the ticket."
            }
          />
          <CardContent>
            <Stack spacing={3}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: scanCode
                    ? "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)"
                    : "linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)",
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 200,
                      height: 200,
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {scanCode ? (
                      <QRCodeCanvas value={scanCode} size={168} level="M" />
                    ) : (
                      <QrCode2RoundedIcon sx={{ fontSize: 72, color: "text.disabled" }} />
                    )}
                  </Box>
                  <Stack spacing={0.75} alignItems="center" textAlign="center">
                    <Typography variant="h6">{previewName || "Add a batch name"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {scanCode
                        ? generatedTicket?.sundryShortUrl
                          ? "This QR is managed by Sundry QR and resolves through its short-link system."
                          : "This QR uses the legacy compact ticket token because Sundry QR is not configured."
                        : "A compact QR token will appear here after generation."}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={
                    previewUnlimitedTotalScans
                      ? "Quantity: Unlimited"
                      : `Quantity: ${previewTicketsCount.toLocaleString()}`
                  }
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={
                    previewUserScanCount && previewUserScanCount > 0
                      ? `User scan limit: ${previewUserScanCount}`
                      : "Unlimited scans"
                  }
                  variant="outlined"
                />
                <Chip label={`Cooldown: ${previewScanCooldownMinutes} min`} variant="outlined" />
                {previewValue !== null ? (
                  <Chip label={`Value: ${previewValue}`} variant="outlined" />
                ) : null}
                {previewPoints !== null ? (
                  <Chip label={`Points: ${previewPoints}`} variant="outlined" />
                ) : null}
                {previewRaffleTicketsCount !== null ? (
                  <Chip
                    label={`Tickets per scan: ${previewRaffleTicketsCount}`}
                    variant="outlined"
                  />
                ) : null}
              </Stack>

              {previewTicketTypes.length ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {previewTicketTypes.map((type) => (
                    <Chip key={type} label={type} size="small" />
                  ))}
                </Stack>
              ) : (
                <Alert severity="warning">Select at least one ticket type to generate.</Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle2">Code</Typography>
                  <TextField
                    value={previewCodePrefix}
                    label="Prefix"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    helperText="Prepended in front of the raw compact token when building the final scan code."
                  />
                  <TextField
                    value={scanCode}
                    label={generatedTicket?.sundryShortUrl ? "Sundry QR short URL" : "Generated scan code"}
                    fullWidth
                    multiline
                    minRows={2}
                    InputProps={{ readOnly: true }}
                    helperText={
                      scanCode
                        ? generatedTicket?.sundryShortUrl
                          ? "This is the public value encoded in the QR. Sundry QR owns the redirect, status, and scan record."
                          : "This is the legacy value encoded in the QR and used when the ticket is scanned."
                        : "Generate tickets to create the final scan code."
                    }
                  />
                  <TextField
                    value={rawQrCode}
                    label="Raw compact ticket token"
                    fullWidth
                    multiline
                    minRows={2}
                    InputProps={{ readOnly: true }}
                    helperText="Original compact token before the prefix is prepended."
                  />
                  {generatedTicket?.sundryQrId ? (
                    <TextField
                      value={generatedTicket.sundryQrId}
                      label="Sundry QR ID"
                      fullWidth
                      InputProps={{ readOnly: true }}
                      helperText={`Status: ${generatedTicket.sundryQrStatus || "unknown"}${
                        generatedTicket.sundryShortCode
                          ? ` · Short code: ${generatedTicket.sundryShortCode}`
                          : ""
                      }`}
                    />
                  ) : null}
                </Stack>
              </Paper>

              {scanCode ? (
                <>
                  {copyFeedback ? (
                    <Alert severity={copyFeedback.severity}>{copyFeedback.message}</Alert>
                  ) : null}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <Button
                      variant="contained"
                      startIcon={<ContentCopyRoundedIcon />}
                      onClick={handleCopyQrCode}
                    >
                      Copy Scan Code
                    </Button>
                    {rawQrCode && rawQrCode !== scanCode ? (
                      <Button variant="outlined" onClick={handleCopyRawQrCode}>
                        Copy Raw Token
                      </Button>
                    ) : null}
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setGeneratedTicket(null)
                        setCopyFeedback(null)
                      }}
                    >
                      Clear Preview
                    </Button>
                  </Stack>
                </>
              ) : (
                <Alert severity="info">
                  The generated batch name will appear in the ticket list below so operators can
                  search for it later.
                </Alert>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid2>
    </Grid2>
  )
}

export default TicketForm
